/**
 * Feb 25, 2026 · Reversal Audit Report — plant-head dashboard listing
 * every Voided doc across Sales Dispatch, Material Issue, Production
 * Entry, and GRN. Rows are grouped by day for compliance review. The
 * backend endpoint (`/api/reports/reversal-audit`) enforces
 * Administrator/Manager/Supervisor role.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowCounterClockwiseIcon, ArrowsClockwiseIcon, WarningIcon } from "@phosphor-icons/react";

const MODULE_TONE = {
  "Material Issue":         "border-sky-500/40 bg-sky-500/10 text-sky-300",
  "Production Entry":       "border-amber-500/40 bg-amber-500/10 text-amber-300",
  "Sales Dispatch":         "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  "GRN / Purchase Receipt": "border-purple-500/40 bg-purple-500/10 text-purple-300",
};

export default function ReversalAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/reversal-audit", {
        params: { start: start || undefined, end: end || undefined },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 space-y-6" data-testid="reversal-audit-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 text-amber-300">Compliance · Reversal Audit</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Reversal Audit Report</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Every doc marked <span className="text-amber-300 font-mono">Voided</span> by Live Transaction Reversal across GRN, Material Issue, Production Entry, and Sales Dispatch — grouped by day with reason, actor, and back-pointer for compliance review.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <input type="date" data-testid="ra-start" value={start} onChange={e => setStart(e.target.value)} className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm" />
          <span className="text-zinc-500">→</span>
          <input type="date" data-testid="ra-end" value={end} onChange={e => setEnd(e.target.value)} className="h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm" />
          <button data-testid="ra-run" onClick={load} className="h-8 px-3 rounded-sm bg-[var(--accent)] text-white text-xs flex items-center gap-1.5">
            <ArrowsClockwiseIcon size={12} weight="bold" />Run
          </button>
        </div>
      </div>

      {loading && <div className="text-center text-zinc-500 py-8 text-sm" data-testid="ra-loading">Loading…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-4">
              <div className="overline text-amber-300">Total Voided</div>
              <div className="font-display text-3xl font-black text-amber-200 tabular-nums" data-testid="ra-total">
                {(data.total || 0).toLocaleString("en-IN")}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">across {(data.days || []).length} day(s)</div>
            </div>
            {Object.entries(data.by_module || {}).map(([m, n]) => (
              <div key={m} className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-4" data-testid={`ra-module-${m.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <div className="overline">{m}</div>
                <div className="font-display text-2xl font-black tabular-nums">{n.toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {(data.days || []).length === 0 && (
              <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-6 text-center text-emerald-300 text-sm">
                No reversals recorded in this window. Zero-defect compliance day.
              </div>
            )}
            {(data.days || []).map(dayGroup => (
              <div key={dayGroup.day} data-testid={`ra-day-${dayGroup.day}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-mono text-white flex items-center gap-2">
                    <WarningIcon size={12} weight="fill" className="text-amber-300" />
                    {dayGroup.day || "unknown"}
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
                      {dayGroup.count} reversal(s) · {(dayGroup.modules || []).join(" · ")}
                    </span>
                  </div>
                </div>
                <div className="border border-[var(--border)] rounded-sm overflow-hidden">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[var(--bg-2)]/60 text-[9px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left w-40">Module</th>
                        <th className="px-3 py-1.5 text-left">Doc No</th>
                        <th className="px-3 py-1.5 text-left w-40">RM / Item</th>
                        <th className="px-3 py-1.5 text-right w-32">Rolled-Back Qty</th>
                        <th className="px-3 py-1.5 text-left">Voided By</th>
                        <th className="px-3 py-1.5 text-left w-24">Voided At</th>
                        <th className="px-3 py-1.5 text-left">Reason</th>
                        <th className="px-3 py-1.5 text-left w-40">Reversal Ref</th>
                        <th className="px-3 py-1.5 text-right w-24">Un-void</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.rows || []).filter(r => r.day === dayGroup.day).map((r, i) => (
                        <tr key={r.doc_id + i} className="border-t border-[var(--border)]" data-testid={`ra-row-${r.doc_id}`}>
                          <td className="px-3 py-1.5">
                            <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${MODULE_TONE[r.module] || ""}`}>{r.module}</span>
                          </td>
                          <td className="px-3 py-1.5 font-mono text-[var(--accent)]">{r.doc_no || "—"}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px] text-zinc-400 truncate">{r.rm_code || "—"}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums text-red-300 font-semibold" data-testid={`ra-qty-${r.doc_id}`}>
                            {Number(r.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                            <span className="text-[9px] text-zinc-500 ml-1">{r.uom || "kg"}</span>
                          </td>
                          <td className="px-3 py-1.5 text-zinc-300">{r.voided_by || "—"}</td>
                          <td className="px-3 py-1.5 font-mono text-zinc-500">{(r.voided_at || "").slice(11, 19)}</td>
                          <td className="px-3 py-1.5 text-zinc-300 truncate max-w-md">{r.reason || <span className="text-zinc-600 italic">no reason recorded</span>}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px] text-zinc-500 truncate">
                            {r.reversed_by_entry_id ? (
                              <span className="inline-flex items-center gap-1 text-amber-300">
                                <ArrowCounterClockwiseIcon size={10} weight="bold" />
                                {r.reversed_by_entry_id.slice(0, 8)}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              data-testid={`ra-undo-${r.doc_id}`}
                              disabled={!r.undo_eligible}
                              title={r.undo_eligible
                                ? `Undo this reversal (Admin-only · ${data.undo_window_hours || 24}h window)`
                                : `Undo window expired · ${data.undo_window_hours || 24}h max`}
                              onClick={async () => {
                                if (!window.confirm(
                                  `Undo reversal for ${r.doc_no || r.doc_id}?\n\n` +
                                  `Deletes the mirror ledger + Kardex entries, re-consumes lot balances back to pre-reversal state, and clears the void stamp so the doc becomes Active again. Only works within 24h of voiding.`,
                                )) return;
                                try {
                                  const { data: res } = await api.post(
                                    `/reports/reversal-audit/${r.collection}/${r.doc_id}/undo`,
                                    { reason: "Wrong reversal" },
                                  );
                                  toast.success(`Un-voided · ${res.stock_moves_deleted} legs + ${res.kardex_deleted} Kardex row(s) removed`);
                                  load();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Undo failed"); }
                              }}
                              className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${r.undo_eligible ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10" : "border-zinc-700 text-zinc-600 cursor-not-allowed"}`}>
                              Un-void
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
